import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { Link } from '@tanstack/react-router'

export const Route = createFileRoute('/plugins')({
  component: PluginsPage,
})

/* --- tiny helpers --------------------------------------------------------- */

function SectionHeading({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="text-3xl font-bold text-gray-900 mb-6 scroll-mt-24"
    >
      {children}
    </h2>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="bg-gray-800 text-gray-100 rounded-lg p-5 block overflow-x-auto text-sm leading-relaxed font-mono whitespace-pre">
      {children}
    </code>
  );
}

function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">
      {children}
    </code>
  );
}

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 items-start">
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center">
        {number}
      </span>
      <div className="pt-0.5 text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

/* --- page ----------------------------------------------------------------- */

function PluginsPage() {
  return (
    <>
      <Helmet>
        <title>Genie Plugins - Build Custom Canvas Nodes</title>
        <meta
          name="description"
          content="Build custom nodes for the Genie canvas. One manifest file, one fully working node."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/60">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-lg font-bold text-gray-900">
              Genie
            </Link>
            <div className="flex items-center gap-4">
              <a
                href="#quickstart"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Quick Start
              </a>
              <a
                href="#manifest"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Manifest
              </a>
              <a
                href="#submit"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Submit
              </a>
              <a
                href="https://github.com/genieartinc/genie"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-700 transition-colors"
              >
                GitHub
              </a>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-6 py-16 space-y-24">
          {/* Hero */}
          <section className="text-center pt-8">
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Genie Plugins
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Build custom nodes for the Genie canvas. One manifest file = one
              fully working node.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a
                href="#quickstart"
                className="px-6 py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors"
              >
                Get Started
              </a>
              <a
                href="https://github.com/genieartinc/genie"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 font-medium hover:bg-gray-50 transition-colors"
              >
                View on GitHub
              </a>
            </div>
          </section>

          {/* What Are Plugins? */}
          <section>
            <SectionHeading>What Are Plugins?</SectionHeading>
            <p className="text-gray-700 leading-relaxed mb-6">
              Plugins are custom canvas nodes defined by a single TypeScript
              manifest. You describe your node&apos;s inputs, outputs, parameters,
              and execution handler &mdash; Genie auto-generates the full UI,
              connection handles, and settings panel. No React code required
              unless you want a custom component.
            </p>
            <Code>{`import { definePlugin } from '@genieartinc/schemas'

export const MyPlugin = definePlugin({
  id: 'community.myname.blur',
  name: 'Gaussian Blur',
  category: 'image/edit',
  icon: 'blur',
  version: '1.0.0',
  inputs:     [ /* ... */ ],
  outputs:    [ /* ... */ ],
  parameters: [ /* ... */ ],
  execute:    { type: 'internal', handler: 'image.blur' },
})`}</Code>
          </section>

          {/* Quick Start */}
          <section id="quickstart">
            <SectionHeading id="quickstart">Quick Start</SectionHeading>
            <div className="space-y-5">
              <Step number={1}>
                <p>
                  <strong>Scaffold a new plugin</strong>
                </p>
                <Code>npx genie-kit create my-plugin</Code>
              </Step>
              <Step number={2}>
                <p>
                  <strong>
                    Edit <InlineCode>plugin.ts</InlineCode>
                  </strong>{" "}
                  &mdash; define inputs, outputs, and parameters.
                </p>
              </Step>
              <Step number={3}>
                <p>
                  <strong>Test locally</strong> in your canvas.
                </p>
                <Code>npx genie-kit dev</Code>
              </Step>
              <Step number={4}>
                <p>
                  <strong>Submit a PR</strong> to the Genie repo to publish your
                  plugin to all users.
                </p>
              </Step>
            </div>
          </section>

          {/* Manifest Example */}
          <section id="manifest">
            <SectionHeading id="manifest">Plugin Manifest Example</SectionHeading>
            <p className="text-gray-700 mb-6">
              A complete manifest for a Background Remover node. This single file
              defines everything Genie needs to render and execute the node.
            </p>
            <Code>{`import { definePlugin } from '@genieartinc/schemas'

export const BackgroundRemoverPlugin = definePlugin({
  id: 'genie.image.background-remover',
  name: 'Background Remover',
  category: 'image/edit',
  icon: 'scan',
  version: '1.0.0',
  description: 'Automatically remove the background from an image.',

  dimensions: { width: 500, height: 200 },

  inputs: [
    {
      id: 'image',
      direction: 'input',
      type: 'image',
      label: 'Image',
      required: true,
      multiple: true,
      position: { side: 'left', offset: 0.5 },
    },
  ],

  outputs: [
    {
      id: 'result',
      direction: 'output',
      type: 'image',
      label: 'Result (PNG)',
      position: { side: 'right', offset: 0.5 },
    },
  ],

  parameters: [
    {
      id: 'model',
      type: 'model-select',
      label: 'Model',
      default: 'birefnet',
      description: 'Background removal model to use.',
    },
    {
      id: 'refine_edges',
      type: 'toggle',
      label: 'Refine Edges',
      default: true,
      description: 'Apply edge refinement for cleaner cutouts.',
    },
    {
      id: 'output_format',
      type: 'select',
      label: 'Output Format',
      default: 'png',
      options: [
        { value: 'png', label: 'PNG (transparent)' },
        { value: 'webp', label: 'WebP (transparent)' },
      ],
      description: 'Output file format.',
    },
  ],

  execute: {
    type: 'internal',
    handler: 'image.background-remover',
  },
})`}</Code>
          </section>

          {/* Parameter Types */}
          <section>
            <SectionHeading>Available Parameter Types</SectionHeading>
            <p className="text-gray-700 mb-6">
              Parameters define the controls shown in the node&apos;s settings
              panel. Each type maps to a specific UI widget.
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-900">Type</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Widget</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ["text", "Text input", "Single-line text field with optional placeholder"],
                    ["textarea", "Multi-line text", "Expandable text area for longer content"],
                    ["number", "Number input", "Numeric field with optional min/max/step"],
                    ["slider", "Range slider", "Draggable slider for numeric ranges"],
                    ["select", "Dropdown", "Pick from predefined options"],
                    ["toggle", "On/off switch", "Boolean toggle for feature flags"],
                    ["color", "Color picker", "Visual color selector, returns hex string"],
                    ["image-upload", "File upload", "Drag-and-drop image upload widget"],
                    ["model-select", "Model dropdown", "Curated list of available AI models"],
                  ].map(([type, widget, desc]) => (
                    <tr key={type} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-mono text-indigo-700 text-xs">
                        {type}
                      </td>
                      <td className="px-4 py-3 text-gray-800">{widget}</td>
                      <td className="px-4 py-3 text-gray-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Handle Types */}
          <section>
            <SectionHeading>Handle Types</SectionHeading>
            <p className="text-gray-700 mb-6">
              Handles are the connection dots on node edges. Each handle has a{" "}
              <InlineCode>type</InlineCode> that determines which connections are
              valid.
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-900">Type</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Data</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Example Use</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ["image", "Raster image (PNG, JPEG, WebP)", "Photo editing, AI generation"],
                    ["video", "Video data (MP4, WebM)", "Video editing, frame extraction"],
                    ["text", "Plain text or prompt strings", "LLM input/output, captions"],
                    ["audio", "Audio data (WAV, MP3)", "Music generation, TTS"],
                    ["mask", "Grayscale mask (white = included)", "Selective processing, inpaint"],
                    ["number", "Numeric value", "Math nodes, counters"],
                    ["any", "Accepts any type", "Merge/split utility nodes"],
                  ].map(([type, data, example]) => (
                    <tr key={type} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-mono text-indigo-700 text-xs">
                        {type}
                      </td>
                      <td className="px-4 py-3 text-gray-800">{data}</td>
                      <td className="px-4 py-3 text-gray-600">{example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* How to Submit */}
          <section id="submit">
            <SectionHeading id="submit">How to Submit</SectionHeading>
            <p className="text-gray-700 mb-8">
              Plugins are currently accepted via Pull Request.
            </p>
            <div className="space-y-5 mb-10">
              <Step number={1}>
                <strong>Fork</strong> the Genie repository on GitHub.
              </Step>
              <Step number={2}>
                Add your manifest to{" "}
                <InlineCode>
                  apps/web-app/src/lib/canvas/plugins/nodes/
                </InlineCode>
              </Step>
              <Step number={3}>
                Register your plugin in{" "}
                <InlineCode>registry.ts</InlineCode>
              </Step>
              <Step number={4}>
                <strong>Submit a PR</strong> &mdash; we&apos;ll review and merge.
              </Step>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200 p-6">
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong>Local-only mode:</strong> You can also use plugins without
                submitting &mdash; run{" "}
                <InlineCode>genie-kit dev</InlineCode> and connect via the{" "}
                <strong>Dev Plugin Panel</strong> in your canvas. Your plugin
                loads in real-time without a PR.
              </p>
            </div>
          </section>

          {/* Resources */}
          <section>
            <SectionHeading>Resources</SectionHeading>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  title: "AI Generation Prompt",
                  description:
                    "Paste PLUGIN_PROMPT.md into any AI to generate a valid plugin manifest.",
                  href: "https://github.com/genieartinc/genie/blob/main/docs/plugin/PLUGIN_PROMPT.md",
                },
                {
                  title: "GenieKit SDK",
                  description:
                    "Type-safe manifest builders, React hooks, and validation utilities.",
                  href: "https://www.npmjs.com/package/@genieartinc/genie-kit",
                },
                {
                  title: "GitHub Repository",
                  description:
                    "Source code, examples, and issue tracker.",
                  href: "https://github.com/genieartinc/genie",
                },
                {
                  title: "Plugin Examples",
                  description:
                    "Browse existing plugins: Background Remover, Style Transfer, Noise Generator.",
                  href: "https://github.com/genieartinc/genie/tree/main/apps/web-app/src/lib/canvas/plugins/nodes",
                },
              ].map((resource) => (
                <a
                  key={resource.title}
                  href={resource.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-5 rounded-lg border border-gray-200 bg-white/60 backdrop-blur-sm hover:bg-white/90 hover:border-indigo-200 transition-all group"
                >
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-700 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-gray-600">{resource.description}</p>
                </a>
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer className="border-t border-gray-200/60 pt-8 pb-4 text-center text-sm text-gray-500">
            <p>
              Genie &mdash; AI Canvas Platform &middot;{" "}
              <Link to="/" className="text-indigo-600 hover:underline">
                Home
              </Link>
            </p>
          </footer>
        </div>
      </main>
    </>
  )
}
